import React, { useState, useEffect, useCallback } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { RefreshCw, ArrowLeft, ArrowRight, RotateCcw, Bell, Mail, Circle, Plus, Link2, GitBranch, Move, Send, Users, Calculator, X, ChevronDown, MoreHorizontal, Search, Trash2 } from 'lucide-react'
import { usePipelineContext } from '../PipelineContext'
import axios from '../../../../config/axios'
import styled from 'styled-components'
import InfiniteSelect from '../../../../components/InfiniteSelect'
import { getModuleById } from '../../../../components/Pipelines/Modals/integrationModulesConfig'

interface Condition {
  id: string
  field: string
  operator: string
  value: string
  logicOperator?: 'AND' | 'OR'
}

interface ConditionRow {
  id: string
  field: string
  operator: string
  value: string
  logicOperator?: 'AND' | 'OR'
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: transparent;
`

const SubHeaderContainer = styled.div`
  background-color: #fff;
  padding: 1.3rem 4.1rem;
  display: flex;
  justify-content: space-between;
  border-radius: .8rem;
  position: relative;
  z-index: 1;
`

const LeftSide = styled.div`
  display: flex;
  align-items: center;
`

const RightSide = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`

const SectionName = styled.h2`
  font-size: 1.4rem;
  font-weight: 700;
  margin: 0;
`

const CancelButton = styled.button`
  font-weight: bold;
  user-select: none;
  display: flex;
  height: 100%;
  align-items: center;
  font-size: .8rem;
  padding: 0.5rem 1.5rem;
  color: #0090D3;
  cursor: pointer;
  background-color: transparent;
  border: 2px solid #0090D3;
  border-radius: 2rem;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background-color: #E6F4FB;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const CreateButton = styled.button`
  font-weight: bold;
  user-select: none;
  display: flex;
  height: 100%;
  align-items: center;
  font-size: .8rem;
  padding: 0.5rem 1.5rem;
  color: #fff;
  cursor: pointer;
  background-color: #0090D3;
  border: none;
  border-radius: 2rem;
  gap: 0.5rem;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background-color: #007bb5;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const OptionsMenuContainer = styled.div`
  position: relative;
`

const OptionsMenuButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background-color: transparent;
  border: 2px solid #0090D3;
  border-radius: 50%;
  color: #0090D3;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: #E6F4FB;
  }
`

const OptionsMenuDropdown = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  min-width: 150px;
  z-index: 100;
  overflow: hidden;
`

const OptionsMenuItem = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 12px 16px;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 14px;
  color: #dc3545;
  transition: background-color 0.2s;

  &:hover {
    background-color: #f5f5f5;
  }
`

const BreadcrumbsContainer = styled.nav`
  font-size: 14px;
  padding: 1rem 0;
  background-color: transparent;
  
  ol {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    align-items: center;
  }
  
  li {
    display: flex;
    align-items: center;
    color: #666;
    
    &:not(:last-child)::after {
      content: '>';
      margin: 0 8px;
      color: #999;
    }
    
    a {
      text-decoration: none;
      color: #0090d3;
      cursor: pointer;

      &:hover {
        text-decoration: underline;
      }
    }
    
    span {
      color: #333;
    }
  }
`

const Content = styled.div`
  flex: 1;
  padding: 0;
  overflow: auto;
  background-color: transparent;
`

const FormCard = styled.div`
  background-color: #fff;
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  display: flex;
  gap: 2rem;
`

const FormGroup = styled.div<{ $flex?: number }>`
  display: flex;
  flex-direction: column;
  flex: ${props => props.$flex || 1};

  label {
    font-size: 14px;
    font-weight: 600;
    color: #333;
    margin-bottom: 8px;
  }

  input {
    padding: 12px 16px;
    border: none;
    border-radius: 100px;
    font-size: 14px;
    background-color: #EBEBEB;
    color: #333;

    &::placeholder {
      color: #999;
    }

    &:focus {
      outline: none;
      box-shadow: 0 0 0 2px rgba(0, 144, 211, 0.2);
    }
  }
`

const SelectionContainer = styled.div`
  display: flex;
  gap: 1.5rem;
  justify-content: center;
  align-items: flex-start;
  padding: 2rem 0;
`

const SelectionCard = styled.div`
  width: 320px;
  background-color: #fff;
  border-radius: 8px;
  padding: 1.5rem;
  height: fit-content;
`

const EventConfigCard = styled.div`
  width: 320px;
  background-color: #fff;
  border-radius: 8px;
  padding: 1.5rem;
  height: fit-content;
`

const EventConfigHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
`

const EventConfigTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  font-weight: 600;
  color: #333;

  svg {
    color: #696969;
    width: 18px;
    height: 18px;
  }
`

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  color: #696969;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #333;
  }
`

const SelectLabel = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
  display: block;
`

const SelectWrapper = styled.div`
  position: relative;
`

const FieldInput = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: none;
  border-radius: 100px;
  font-size: 14px;
  background-color: #EBEBEB;
  color: #333;
  box-sizing: border-box;

  &::placeholder {
    color: #999;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(0, 144, 211, 0.2);
  }
`

const SearchInputWrapper = styled.div`
  position: relative;
  width: 100%;
`

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 40px 12px 16px;
  border: none;
  border-radius: 100px;
  font-size: 14px;
  background-color: #EBEBEB;
  color: #333;
  box-sizing: border-box;

  &::placeholder {
    color: #999;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(0, 144, 211, 0.2);
  }
`

const SearchIcon = styled.div`
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #999;
  pointer-events: none;
`

const StyledSelect = styled.select`
  width: 100%;
  padding: 12px 40px 12px 16px;
  border: none;
  border-radius: 100px;
  font-size: 14px;
  background-color: #EBEBEB;
  color: #333;
  appearance: none;
  cursor: pointer;

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(0, 144, 211, 0.2);
  }
`

const SelectIcon = styled.div`
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  color: #696969;
`

const SelectionTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin: 0 0 1rem 0;
`

const SelectionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`

const SelectionItem = styled.div<{ $selected?: boolean; $disabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 8px;
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  background-color: ${props => props.$selected ? '#E6F4FB' : '#F5F5F5'};
  border: 1px solid ${props => props.$selected ? '#0090D3' : 'transparent'};
  opacity: ${props => props.$disabled ? 0.5 : 1};

  &:hover {
    background-color: ${props => props.$disabled ? '#F5F5F5' : props.$selected ? '#E6F4FB' : '#EBEBEB'};
  }

  svg {
    color: ${props => props.$disabled ? '#BEBEBE' : '#696969'};
    width: 18px;
    height: 18px;
  }

  span {
    font-size: 14px;
    color: ${props => props.$disabled ? '#BEBEBE' : '#333'};
  }
`

const ConditionsSeparator = styled.div`
  width: 100%;
  height: 1px;
  background-color: #E5E5E5;
  margin: 20px 0;
`

const ConditionsSection = styled.div`
  margin-top: 0;
`

const ConditionsSectionTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 12px;
`

const ConditionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
`

const ConditionsGroupContainer = styled.div`
  background-color: #F5F5F5;
  border-radius: 8px;
  border-left: 3px solid #0090D3;
  overflow: hidden;
  margin-bottom: 16px;
`

const ConditionsGroupHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
`

const ConditionsGroupContent = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 12px 12px 12px;
`

const ConditionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #333;
  padding: 4px 0;

  span {
    color: #696969;
  }
`

const ConditionChip = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  background-color: #F5F5F5;
  border-radius: 8px;
  border-left: 3px solid #0090D3;
`

const ConditionChipContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #333;

  span {
    color: #696969;
  }
`

const ConditionChipActions = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  color: #696969;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #333;
  }
`

const AddConditionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  padding: 12px 16px;
  background-color: transparent;
  border: 2px solid #0090D3;
  border-radius: 100px;
  color: #0090D3;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: #E6F4FB;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`

const ModalContainer = styled.div`
  background-color: #fff;
  border-radius: 12px;
  padding: 24px;
  width: 560px;
  max-width: 90vw;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
`

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`

const ModalTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0;
`

const ModalCloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  color: #696969;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #333;
  }
`

const ModalSubtitle = styled.p`
  font-size: 14px;
  color: #696969;
  margin: 0 0 24px 0;
`

const ModalFieldsRow = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
`

const ModalFieldGroup = styled.div<{ $flex?: number }>`
  display: flex;
  flex-direction: column;
  flex: ${props => props.$flex || 1};
`

const ModalFieldLabel = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
`

const ModalSelect = styled.select`
  padding: 12px 40px 12px 16px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  background-color: #EBEBEB;
  color: #333;
  appearance: none;
  cursor: pointer;
  width: 100%;

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(0, 144, 211, 0.2);
  }
`

const ModalInput = styled.input`
  padding: 12px 16px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  background-color: #EBEBEB;
  color: #333;
  width: 100%;

  &::placeholder {
    color: #999;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(0, 144, 211, 0.2);
  }
`

const ModalSelectWrapper = styled.div`
  position: relative;
`

const ModalSelectIcon = styled.div`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  color: #696969;
`

const LogicButtonsRow = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 24px;
`

const LogicButton = styled.button<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px 16px;
  background-color: ${props => props.$active ? '#E6F4FB' : 'transparent'};
  border: 2px solid #0090D3;
  border-radius: 100px;
  color: #0090D3;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: #E6F4FB;
  }

  svg {
    width: 14px;
    height: 14px;
  }
`

const LogicOperatorLabel = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #0090D3;
  margin: 8px 0;
`

const LogicOperatorBetween = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #0090D3;
  text-align: center;
  padding: 4px 0;
`

const RemoveRowButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background-color: transparent;
  border: none;
  border-radius: 4px;
  color: #999;
  cursor: pointer;
  margin-top: auto;
  margin-bottom: 4px;

  &:hover {
    background-color: #f0f0f0;
    color: #666;
  }
`

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
`

const ModalCancelButton = styled.button`
  padding: 10px 24px;
  background-color: transparent;
  border: 2px solid #0090D3;
  border-radius: 100px;
  color: #0090D3;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: #E6F4FB;
  }
`

const ModalAddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 24px;
  background-color: #0090D3;
  border: none;
  border-radius: 100px;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: #007bb5;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`

const ModalDeleteButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 24px;
  background-color: transparent;
  border: 2px solid #dc3545;
  border-radius: 100px;
  color: #dc3545;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: #fff5f5;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`

const fieldOptions = [
  { value: 'valor', label: 'Valor' },
  { value: 'status', label: 'Status' },
  { value: 'responsavel', label: 'Responsável' },
  { value: 'prioridade', label: 'Prioridade' },
  { value: 'data_criacao', label: 'Data de criação' },
  { value: 'etapa', label: 'Etapa' },
]

const operatorOptions = [
  { value: 'equals', label: 'é igual a' },
  { value: 'not_equals', label: 'é diferente de' },
  { value: 'greater_than', label: 'é maior que' },
  { value: 'less_than', label: 'é menor que' },
  { value: 'contains', label: 'contém' },
  { value: 'not_contains', label: 'não contém' },
]

const events = [
  { id: 'card_created', label: 'Card é criado', icon: RefreshCw },
  { id: 'card_enter_phase', label: 'Card entra na etapa', icon: ArrowLeft },
  { id: 'card_exit_phase', label: 'Card sai da etapa', icon: ArrowRight },
  { id: 'field_updated', label: 'Campo é atualizado', icon: RotateCcw },
  { id: 'recurring_activity', label: 'Atividade recorrente', icon: RefreshCw },
  { id: 'alert_triggered', label: 'Alerta acionado', icon: Bell },
  { id: 'email_received', label: 'E-mail recebido', icon: Mail },
  { id: 'extra_event', label: 'Evento extra', icon: Circle },
]

const actions = [
  { id: 'create_card', label: 'Criar um card', icon: Plus },
  { id: 'create_connected_card', label: 'Criar um card conectado', icon: Link2 },
  { id: 'move_parent_card', label: 'Mover um card pai', icon: GitBranch },
  { id: 'update_field', label: 'Atualizar um campo', icon: RotateCcw },
  { id: 'move_card', label: 'Mover o card', icon: Move },
  { id: 'send_email', label: 'Enviar um e-mail', icon: Send },
  { id: 'distribute_responsible', label: 'Distribua responsáveis', icon: Users },
  { id: 'create_formula', label: 'Crie uma fórmula', icon: Calculator },
]

const eventsWithStepConfig = ['card_created', 'field_updated']

const eventsWithPhaseConfig = ['card_enter_phase', 'card_exit_phase']

const eventsWithFieldConfig = ['field_updated']

const actionsWithPipelineConfig = ['create_connected_card']
const actionsWithFieldConfig = ['update_field', 'create_formula']
const actionsWithPhaseConfig = ['move_card']
const actionsWithStepConfig = ['create_card']

interface Pipeline {
  id: number
  name: string
}

interface Step {
  id: number
  name: string
}

interface Field {
  id: number
  name: string
  type: string
  typeFieldId: number
  integrationConfig: any
}

const Breadcrumbs: React.FC<{ items: { label: string; path?: string; onClick?: () => void }[] }> = ({
  items,
}) => (
  <BreadcrumbsContainer>
    <ol>
      {items.map((item, index) => (
        <li key={index}>
          {index === items.length - 1 ? (
            <span>{item.label}</span>
          ) : item.path ? (
            <Link to={item.path}>{item.label}</Link>
          ) : item.onClick ? (
            <a onClick={item.onClick}>{item.label}</a>
          ) : (
            <span style={{color: '#0090d3', cursor: 'pointer'}}>{item.label}</span>
          )}
        </li>
      ))}
    </ol>
  </BreadcrumbsContainer>
)

const CreateAutomation: React.FC = () => {
  const [searchParams] = useSearchParams()
  const pipelineId = searchParams.get('pipelineId')
  const { pipelineName, setIsCreatingAutomation, tenantId, editingAutomationId, setEditingAutomationId } = usePipelineContext()
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [selectedEvent, setSelectedEvent] = useState<string | null>(null)
  const [selectedAction, setSelectedAction] = useState<string | null>(null)
  const [selectedStep, setSelectedStep] = useState<string>('')
  const [selectedPhase, setSelectedPhase] = useState<string>('')
  const [selectedField, setSelectedField] = useState<string>('')
  const [selectedFieldValue, setSelectedFieldValue] = useState<string>('')
  const [selectedIntegrationValue, setSelectedIntegrationValue] = useState<any>(null)
  const [steps, setSteps] = useState<Step[]>([])
  const [fields, setFields] = useState<Field[]>([])
  const [conditions, setConditions] = useState<Condition[]>([])
  const [isConditionModalOpen, setIsConditionModalOpen] = useState(false)
  const [editingConditionId, setEditingConditionId] = useState<string | null>(null)
  const [modalConditionRows, setModalConditionRows] = useState<ConditionRow[]>([
    { id: 'row_1', field: 'valor', operator: 'greater_than', value: '' }
  ])
  const [showOptionsMenu, setShowOptionsMenu] = useState(false)
  const [isLoadingAutomation, setIsLoadingAutomation] = useState(false)

  const [pipelines, setPipelines] = useState<Pipeline[]>([])
  const [actionPipeline, setActionPipeline] = useState<string>('')
  const [actionPipelineSteps, setActionPipelineSteps] = useState<Step[]>([])
  const [pendingActionPipelineStep, setPendingActionPipelineStep] = useState<string | null>(null)
  const [pendingStepName, setPendingStepName] = useState<string | null>(null)
  const [pendingPhaseName, setPendingPhaseName] = useState<string | null>(null)
  const [actionPipelineStep, setActionPipelineStep] = useState<string>('')
  const [actionCard, setActionCard] = useState<string>('')
  const [cards, setCards] = useState<any[]>([])
  const [actionField, setActionField] = useState<string>('')
  const [actionVariable, setActionVariable] = useState<string>('')
  const [actionPhase, setActionPhase] = useState<string>('')
  const [actionStep, setActionStep] = useState<string>('')
  const [actionCardTitle, setActionCardTitle] = useState<string>('')
  const [actionFormula, setActionFormula] = useState<string>('')
  const [actionDestinationField, setActionDestinationField] = useState<string>('')
  const [actionFields, setActionFields] = useState<Field[]>([])
  const [isCreating, setIsCreating] = useState(false)

  useEffect(() => {
    const fetchSteps = async () => {
      if (!pipelineId) return
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/step?pipelineId=${pipelineId}`
        )
        if (response.data?.data) {
          setSteps(response.data.data)
        }
      } catch (error) {
        console.error(error)
      }
    }
    fetchSteps()
  }, [pipelineId])

  useEffect(() => {
    const loadAutomation = async () => {
      if (!editingAutomationId) return
      setIsLoadingAutomation(true)
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_GATEWAY_BASE_URL}/automation-service/automation/${editingAutomationId}`
        )
        const automation = response.data
        console.log('[EDIT] Dados da automação:', JSON.stringify(automation, null, 2))
        if (automation) {
          setName(automation.name || '')
          setDescription(automation.description || '')
          
          console.log('[EDIT] triggerTable:', automation.triggerTable, 'triggerEvent:', automation.triggerEvent, 'triggerColumn:', automation.triggerColumn)
          
          if (automation.triggerTable === 'Ticket' && automation.triggerEvent === 'INSERT') {
            console.log('[EDIT] Mapeando para card_created')
            setSelectedEvent('card_created')
            console.log('[EDIT] conditions:', automation.conditions)
            if (automation.conditions?.rules) {
              const stepCondition = automation.conditions.rules.find((r: any) => r.field === 'stepName')
              console.log('[EDIT] stepCondition:', stepCondition)
              if (stepCondition) {
                setPendingStepName(stepCondition.value)
              }
            }
          } else if (automation.triggerTable === 'Ticket' && automation.triggerEvent === 'UPDATE' && automation.triggerColumn === 'stepId') {
            console.log('[EDIT] Mapeando para card_enter_phase')
            setSelectedEvent('card_enter_phase')
            if (automation.conditions?.rules) {
              const stepCondition = automation.conditions.rules.find((r: any) => r.field === 'stepName' && r.source === 'after')
              console.log('[EDIT] stepCondition para card_enter_phase:', stepCondition)
              if (stepCondition) {
                setPendingPhaseName(stepCondition.value)
              }
            }
          } else if (automation.triggerTable === 'TicketFieldValue' && automation.triggerEvent === 'UPDATE') {
            console.log('[EDIT] Mapeando para field_updated')
            setSelectedEvent('field_updated')
            if (automation.triggerFieldId) {
              setSelectedField(String(automation.triggerFieldId))
            }
            if (automation.triggerStepId) {
              setSelectedStep(String(automation.triggerStepId))
            }
          } else if (automation.triggerTable === 'TicketFieldValue' && automation.triggerEvent === 'INSERT') {
            console.log('[EDIT] Mapeando para field_filled')
            setSelectedEvent('field_filled')
            if (automation.triggerFieldId) {
              setSelectedField(String(automation.triggerFieldId))
            }
            if (automation.triggerStepId) {
              setSelectedStep(String(automation.triggerStepId))
            }
          }
          
          const conditionsArray = automation.conditions?.rules || (Array.isArray(automation.conditions) ? automation.conditions : [])
          if (conditionsArray.length > 0) {
            const mappedConditions = conditionsArray
              .filter((c: any) => c.field !== 'stepName' && c.field !== 'pipelineName')
              .map((c: any, index: number) => ({
                id: `cond_${index}`,
                field: c.field || 'valor',
                operator: c.operator || 'greater_than',
                value: c.value || '',
                logicOperator: c.logicOperator
              }))
            setConditions(mappedConditions)
          }
          
          console.log('[EDIT] actions:', automation.actions)
          if (automation.actions && automation.actions.length > 0) {
            const action = automation.actions[0]
            console.log('[EDIT] action.action:', action.action, 'payload:', action.payload)
            if (action.action === 'ticket.create') {
              let targetPipelineId = action.payload?.pipelineId
              const targetStepId = action.payload?.stepId
              console.log('[EDIT] ticket.create - targetPipelineId:', targetPipelineId, 'targetStepId:', targetStepId, 'currentPipelineId:', pipelineId)
              
              if (!targetPipelineId && targetStepId) {
                try {
                  const stepResponse = await axios.get(
                    `${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/step/${targetStepId}`
                  )
                  const stepData = stepResponse.data?.data || stepResponse.data
                  if (stepData?.pipelineId) {
                    targetPipelineId = stepData.pipelineId
                    console.log('[EDIT] Obteve pipelineId do step:', targetPipelineId)
                  }
                } catch (err) {
                  console.error('Erro ao buscar pipelineId do step:', err)
                }
              }
              
              const isConnectedCard = targetPipelineId && String(targetPipelineId) !== String(pipelineId)
              console.log('[EDIT] isConnectedCard:', isConnectedCard)
              
              if (isConnectedCard) {
                setSelectedAction('create_connected_card')
                setActionPipeline(String(targetPipelineId))
                if (targetStepId) {
                  setPendingActionPipelineStep(String(targetStepId))
                }
              } else {
                setSelectedAction('create_card')
                if (targetStepId) {
                  setActionStep(String(targetStepId))
                }
              }
              
              if (action.payload?.title) {
                setActionCardTitle(action.payload.title)
              }
            } else if (action.action === 'ticket.update' && action.payload?.stepId && !action.payload?.formula) {
              setSelectedAction('move_card')
              setActionPhase(String(action.payload.stepId))
            } else if (action.action === 'ticket.update' && action.payload?.fieldId) {
              setSelectedAction('update_field')
              if (action.payload?.fieldId) {
                setActionField(String(action.payload.fieldId))
              }
              if (action.payload?.value) {
                setActionVariable(action.payload.value)
              }
            } else if (action.action === 'ticket.update' && action.payload?.formula) {
              setSelectedAction('create_formula')
              if (action.payload?.formula) {
                setActionFormula(action.payload.formula)
              }
              if (action.payload?.destinationFieldId) {
                setActionDestinationField(String(action.payload.destinationFieldId))
              }
            }
          }
        }
      } catch (error) {
        console.error('Erro ao carregar automação:', error)
      } finally {
        setIsLoadingAutomation(false)
      }
    }
    loadAutomation()
  }, [editingAutomationId])

  useEffect(() => {
    if (pendingStepName && steps.length > 0) {
      const stepByName = steps.find(s => s.name === pendingStepName)
      if (stepByName) {
        setSelectedStep(String(stepByName.id))
        setPendingStepName(null)
      } else {
        if (steps.length > 0) {
          setSelectedStep(String(steps[0].id))
          setPendingStepName(null)
        }
      }
    }
  }, [steps, pendingStepName])

  useEffect(() => {
    if (pendingPhaseName && steps.length > 0) {
      const phaseByName = steps.find(s => s.name === pendingPhaseName)
      console.log('[EDIT] Convertendo pendingPhaseName:', pendingPhaseName, '-> phaseByName:', phaseByName)
      if (phaseByName) {
        const phaseId = String(phaseByName.id)
        console.log('[EDIT] Setando selectedPhase para:', phaseId)
        setSelectedPhase(phaseId)
        setPendingPhaseName(null)
      } else {
        if (steps.length > 0) {
          const fallbackId = String(steps[0].id)
          console.log('[EDIT] Fase não encontrada, usando fallback:', fallbackId)
          setSelectedPhase(fallbackId)
          setPendingPhaseName(null)
        }
      }
    }
  }, [steps, pendingPhaseName])

  useEffect(() => {
    const fetchFields = async () => {
      if (!selectedStep || selectedEvent !== 'field_updated') {
        setFields([])
        return
      }
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/step/fields?stepId=${selectedStep}`
        )
        if (response.data?.data) {
          const mappedFields = response.data.data.map((item: any) => ({
            id: item.pipelineField.id,
            name: item.pipelineField.name,
            type: item.pipelineField.integrationConfig ? 'integrated' : 'custom',
            typeFieldId: item.pipelineField.typeFieldId,
            integrationConfig: item.pipelineField.integrationConfig
          }))
          setFields(mappedFields)
        }
      } catch (error) {
        console.error(error)
        setFields([])
      }
    }
    fetchFields()
  }, [selectedStep, selectedEvent])

  useEffect(() => {
    const fetchPipelines = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/pipeline`
        )
        if (response.data?.data) {
          setPipelines(response.data.data)
        }
      } catch (error) {
        console.error(error)
      }
    }
    fetchPipelines()
  }, [])

  useEffect(() => {
    const fetchPipelineSteps = async () => {
      if (!actionPipeline) {
        setActionPipelineSteps([])
        return
      }
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/step?pipelineId=${actionPipeline}`
        )
        if (response.data?.data) {
          setActionPipelineSteps(response.data.data)
        }
      } catch (error) {
        setActionPipelineSteps([])
      }
    }
    fetchPipelineSteps()
  }, [actionPipeline])

  useEffect(() => {
    if (pendingActionPipelineStep && actionPipelineSteps.length > 0) {
      const stepExists = actionPipelineSteps.some(s => String(s.id) === pendingActionPipelineStep)
      if (stepExists) {
        setActionPipelineStep(pendingActionPipelineStep)
        setPendingActionPipelineStep(null)
      }
    }
  }, [actionPipelineSteps, pendingActionPipelineStep])

  useEffect(() => {
    const fetchCards = async () => {
      if (!selectedStep) {
        setCards([])
        return
      }
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket?stepId=${selectedStep}`
        )
        if (response.data?.data) {
          setCards(response.data.data)
        }
      } catch (error) {
        setCards([])
      }
    }
    fetchCards()
  }, [selectedStep])

  useEffect(() => {
    const fetchActionFields = async () => {
      if (!selectedStep) {
        setActionFields([])
        return
      }
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/step/fields?stepId=${selectedStep}`
        )
        if (response.data?.data) {
          const mappedFields = response.data.data.map((item: any) => ({
            id: item.pipelineField.id,
            name: item.pipelineField.name,
            type: item.pipelineField.integrationConfig ? 'integrated' : 'custom',
            typeFieldId: item.pipelineField.typeFieldId,
            integrationConfig: item.pipelineField.integrationConfig
          }))
          setActionFields(mappedFields)
        }
      } catch (error) {
        setActionFields([])
      }
    }
    if (selectedAction && actionsWithFieldConfig.includes(selectedAction)) {
      fetchActionFields()
    }
  }, [selectedStep, selectedAction])

  const breadcrumbItems = [
    { label: 'Funis', path: '/panel/pipelines' },
    { label: pipelineName || 'Nome do funil' },
    { label: 'Automação', onClick: () => setIsCreatingAutomation(false) },
    { label: editingAutomationId ? 'Editar automação' : 'Nova automação' },
  ]

  const handleCancel = () => {
    setEditingAutomationId(null)
    setIsCreatingAutomation(false)
  }

  const handleCreate = async () => {
    if (!name || !selectedEvent || !selectedAction) {
      alert('Preencha todos os campos obrigatórios')
      return
    }

    setIsCreating(true)

    try {
      const eventMapping: Record<string, { table: string; event: string; column?: string }> = {
        'card_created': { table: 'Ticket', event: 'INSERT' },
        'card_enter_phase': { table: 'Ticket', event: 'UPDATE', column: 'stepId' },
        'card_exit_phase': { table: 'Ticket', event: 'UPDATE', column: 'stepId' },
        'field_updated': { table: 'TicketFieldValue', event: 'UPDATE', column: 'value' },
      }

      const eventConfig = eventMapping[selectedEvent] || { table: 'Ticket', event: 'UPDATE' }

      const buildConditions = () => {
        const rules: any[] = []

        if (selectedStep) {
          const stepData = steps.find(s => s.id === Number(selectedStep))
          if (stepData) {
            rules.push({
              field: 'stepName',
              operator: 'equals',
              value: stepData.name,
              source: 'after'
            })
          }
        }

        if (selectedPhase && (selectedEvent === 'card_enter_phase' || selectedEvent === 'card_exit_phase')) {
          const phaseData = steps.find(s => s.id === Number(selectedPhase))
          if (phaseData) {
            rules.push({
              field: 'stepName',
              operator: selectedEvent === 'card_enter_phase' ? 'equals' : 'not_equals',
              value: phaseData.name,
              source: selectedEvent === 'card_enter_phase' ? 'after' : 'before'
            })
          }
        }

        if (pipelineName) {
          rules.push({
            field: 'pipelineName',
            operator: 'equals',
            value: pipelineName,
            source: 'after'
          })
        }

        conditions.forEach(cond => {
          rules.push({
            field: cond.field,
            operator: cond.operator === 'greater_than' ? 'gt' : 
                      cond.operator === 'less_than' ? 'lt' : 
                      cond.operator === 'equals' ? 'equals' : cond.operator,
            value: cond.value,
            source: 'after'
          })
        })

        return {
          operator: 'AND',
          rules
        }
      }

      const buildActions = () => {
        const actions: any[] = []

        if (selectedAction === 'create_card') {
          actions.push({
            type: 'http',
            target: 'pipelines',
            action: 'ticket.create',
            payload: {
              title: actionCardTitle || 'Novo card',
              stepId: actionStep ? Number(actionStep) : 'after.stepId',
              tenantId: 'tenantId'
            }
          })
        }

        if (selectedAction === 'create_connected_card') {
          actions.push({
            type: 'http',
            target: 'pipelines',
            action: 'ticket.create',
            payload: {
              title: actionCardTitle || 'Card conectado',
              pipelineId: actionPipeline ? Number(actionPipeline) : undefined,
              stepId: actionPipelineStep ? Number(actionPipelineStep) : undefined,
              tenantId: 'tenantId'
            }
          })
        }

        if (selectedAction === 'update_field') {
          actions.push({
            type: 'http',
            target: 'pipelines',
            action: 'ticket.update',
            payload: {
              id: actionCard ? Number(actionCard) : 'after.id',
              fieldId: actionField ? Number(actionField) : undefined,
              value: actionVariable || 'after.value'
            }
          })
        }

        if (selectedAction === 'move_card') {
          actions.push({
            type: 'http',
            target: 'pipelines',
            action: 'ticket.update',
            payload: {
              id: actionCard ? Number(actionCard) : 'after.id',
              stepId: actionPhase ? Number(actionPhase) : undefined
            }
          })
        }

        if (selectedAction === 'create_formula') {
          actions.push({
            type: 'http',
            target: 'pipelines',
            action: 'ticket.update',
            payload: {
              id: 'after.id',
              formula: actionFormula,
              destinationFieldId: actionDestinationField ? Number(actionDestinationField) : undefined
            }
          })
        }

        return actions
      }

      const builtActions = buildActions()
      
      if (!builtActions || builtActions.length === 0) {
        alert('É necessário configurar uma ação válida')
        setIsCreating(false)
        return
      }

      const automationPayload = {
        name,
        description,
        triggerTable: eventConfig.table,
        triggerEvent: eventConfig.event,
        triggerColumn: eventConfig.column,
        tenantId,
        module: 'pipelines',
        isActive: true,
        conditions: buildConditions(),
        actions: builtActions
      }

      if (editingAutomationId) {
        await axios.patch(
          `${process.env.REACT_APP_GATEWAY_BASE_URL}/automation-service/automation/${editingAutomationId}`,
          automationPayload
        )
      } else {
        const createResponse = await axios.post(
          `${process.env.REACT_APP_GATEWAY_BASE_URL}/automation-service/automation`,
          automationPayload
        )
        
        const newAutomationId = createResponse.data?.data?.id || createResponse.data?.id
        if (newAutomationId && pipelineId) {
          await axios.post(
            `${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/pipeline/${pipelineId}/automation`,
            { automationId: newAutomationId }
          )
        }
      }

      setEditingAutomationId(null)
      setIsCreatingAutomation(false)
    } catch (error) {
      console.error(editingAutomationId ? 'Erro ao atualizar automação:' : 'Erro ao criar automação:', error)
      alert(editingAutomationId ? 'Erro ao atualizar automação. Tente novamente.' : 'Erro ao criar automação. Tente novamente.')
    } finally {
      setIsCreating(false)
    }
  }

  const handleEventSelect = (eventId: string) => {
    setSelectedEvent(eventId)
    if (!eventsWithStepConfig.includes(eventId)) {
      setSelectedStep('')
    }
  }

  const handleCloseEventConfig = () => {
    setSelectedEvent(null)
    setSelectedStep('')
    setSelectedPhase('')
    setSelectedField('')
    setSelectedFieldValue('')
    setSelectedIntegrationValue(null)
    setFields([])
  }

  const getSelectedEventData = () => {
    return events.find(e => e.id === selectedEvent)
  }

  const getSelectedActionData = () => {
    return actions.find(a => a.id === selectedAction)
  }

  const handleActionSelect = (actionId: string) => {
    setSelectedAction(actionId)
    setActionPipeline('')
    setActionPipelineStep('')
    setActionCard('')
    setActionField('')
    setActionVariable('')
    setActionPhase('')
    setActionStep('')
    setActionCardTitle('')
    setActionFormula('')
    setActionDestinationField('')
  }

  const handleCloseActionConfig = () => {
    setSelectedAction(null)
    setActionPipeline('')
    setActionPipelineStep('')
    setActionCard('')
    setActionField('')
    setActionVariable('')
    setActionPhase('')
    setActionStep('')
    setActionCardTitle('')
    setActionFormula('')
    setActionDestinationField('')
  }

  const variableOptions = [
    { value: 'valor', label: 'Valor' },
    { value: 'nome', label: 'Nome' },
    { value: 'data', label: 'Data' },
    { value: 'responsavel', label: 'Responsável' },
  ]

  const showActionConfig = selectedAction && (
    actionsWithPipelineConfig.includes(selectedAction) ||
    actionsWithFieldConfig.includes(selectedAction) ||
    actionsWithPhaseConfig.includes(selectedAction) ||
    actionsWithStepConfig.includes(selectedAction)
  )

  const getSelectedFieldData = () => {
    return fields.find(f => f.id === Number(selectedField))
  }

  const isIntegratedField = () => {
    const field = getSelectedFieldData()
    return field?.integrationConfig !== null && field?.integrationConfig !== undefined
  }

  const getPrimaryLabel = useCallback((option: any): string => {
    if (!option) return ''
    const label = option.name || option.description || option.companyName || option.productCode || option.codeOfOP || option.plate || ''
    console.log('[getPrimaryLabel] option.id:', option?.id, 'label:', label || String(option.id || ''))
    return label || String(option.id || '')
  }, [])

  const getNestedValue = useCallback((obj: any, path: string): any => {
    const keys = path.split('.')
    let val = obj
    for (const k of keys) {
      if (val === null || val === undefined) return null
      val = val[k]
    }
    return val
  }, [])

  const hasValidFieldValue = useCallback((item: any, fieldKey: string): boolean => {
    const value = getNestedValue(item, fieldKey)
    if (value === null || value === undefined || value === '') return false
    if (Array.isArray(value) && value.length === 0) return false
    if (typeof value === 'object' && !Array.isArray(value)) {
      const objName = value.name || value.typeName || value.companyName || value.id
      return objName !== null && objName !== undefined && objName !== ''
    }
    return true
  }, [getNestedValue])

  const hasValidScalarValue = useCallback((item: any, fieldKey: string): boolean => {
    const value = getNestedValue(item, fieldKey)
    if (value === null || value === undefined) return false
    if (value === '') return false
    if (typeof value === 'number' && value === 0) return false
    if (typeof value === 'object' && !Array.isArray(value)) {
      const objName = value.name || value.typeName || value.companyName || value.id
      return objName !== null && objName !== undefined && objName !== ''
    }
    return true
  }, [getNestedValue])

  const getDisplayValueFromField = useCallback((item: any, fieldKey: string): string => {
    const value = getNestedValue(item, fieldKey)
    if (value === null || value === undefined) return ''
    if (Array.isArray(value)) {
      const names = value.map(v => typeof v === 'object' ? (v.name || v.typeName || v.id || '') : String(v)).filter(Boolean)
      if (names.length === 0) return ''
      if (names.length <= 3) return names.join(', ')
      return `${names.slice(0, 3).join(', ')} +${names.length - 3}`
    }
    if (typeof value === 'object') {
      return value.name || value.typeName || value.companyName || String(value.id || '')
    }
    return String(value)
  }, [getNestedValue])

  const fetchIntegrationData = useCallback(async (limit: number, offset: number, search?: string) => {
    const field = getSelectedFieldData()
    const integrationConfig = field?.integrationConfig
    if (!integrationConfig) return []

    const moduleConfig = getModuleById(integrationConfig.moduleId)
    const fieldKey = integrationConfig.fieldKey
    const url = `${process.env.REACT_APP_GATEWAY_BASE_URL}${moduleConfig?.endpoint || integrationConfig.endpoint}`
    const paginationType = moduleConfig?.paginationType || (integrationConfig as any).paginationType || 'offset'
    console.log('[fetchIntegrationData CA] moduleId:', integrationConfig.moduleId, 'fieldKey:', fieldKey, 'url:', url, 'paginationType:', paginationType)
    const params: any = (() => {
      if (search && search.trim()) {
        if (paginationType === 'page') return { limit: 100, page: 1, search }
        if (paginationType === 'skip-take') return { skip: 0, take: 100, search }
        return { limit: 100, offset: 0, search }
      }
      if (paginationType === 'page') return { limit, page: Math.floor(offset / limit) + 1 }
      if (paginationType === 'skip-take') return { skip: offset, take: limit }
      return { limit, offset }
    })()

    try {
      const response = await axios.get(url, { params })
      const raw = response.data
      const responseKey = moduleConfig?.responseKey ?? (integrationConfig as any).responseKey
      const extracted = (() => {
        if (responseKey) {
          const keyed = raw?.[responseKey]
          if (Array.isArray(keyed)) return keyed
        }
        if (Array.isArray(raw)) return raw
        if (Array.isArray(raw?.data)) return raw.data
        if (Array.isArray(raw?.content)) return raw.content
        if (Array.isArray(raw?.items)) return raw.items
        if (Array.isArray(raw?.results)) return raw.results
        if (raw && typeof raw === 'object') {
          const firstArrayKey = Object.keys(raw).find(k => Array.isArray(raw[k]))
          if (firstArrayKey) return raw[firstArrayKey]
        }
        return raw
      })()
      const dataArray = Array.isArray(extracted) ? extracted : []

      const sampleFieldValue = dataArray.length > 0 ? getNestedValue(dataArray[0], fieldKey) : undefined
      const isArrayField = Array.isArray(sampleFieldValue)
      const fieldConfig = moduleConfig?.fields?.find(f => f.field === fieldKey)

      let filteredData: any[]
      if (isArrayField && fieldConfig?.filterEmpty) {
        const seenIds = new Set<any>()
        const expanded: any[] = []
        for (const record of dataArray) {
          const arr = getNestedValue(record, fieldKey)
          if (!Array.isArray(arr) || arr.length === 0) continue
          for (const element of arr) {
            const elemId = element?.id
            if (elemId !== undefined && seenIds.has(elemId)) continue
            if (elemId !== undefined) seenIds.add(elemId)
            const displayName = typeof element === 'object' ? (element.name || element.typeName || '') : String(element)
            if (!displayName) continue
            expanded.push({ ...element, _parentRecord: record, _displayName: displayName })
          }
        }
        filteredData = expanded
      } else if (isArrayField) {
        filteredData = dataArray
      } else {
        filteredData = dataArray.filter((item: any) => hasValidScalarValue(item, fieldKey))
      }
      console.log('[fetchIntegrationData CA] fieldKey:', fieldKey, 'total:', dataArray.length, 'isArrayField:', isArrayField, 'filterEmpty:', fieldConfig?.filterEmpty, 'após expansão/filtro:', filteredData.length)

      if (search && search.trim()) {
        const searchLower = search.toLowerCase().trim()
        return filteredData.filter((item: any) => {
          const primaryLabel = getPrimaryLabel(item)
          const fieldValue = getDisplayValueFromField(item, fieldKey)
          return primaryLabel.toLowerCase().includes(searchLower) ||
                 fieldValue.toLowerCase().includes(searchLower) ||
                 String(item.id || '').includes(searchLower)
        })
      }

      return filteredData
    } catch (error) {
      return []
    }
  }, [selectedField, fields, getNestedValue, getDisplayValueFromField, getPrimaryLabel, hasValidScalarValue])

  const handleIntegrationSelect = (option: any | null) => {
    const field = getSelectedFieldData()
    const fieldKey = field?.integrationConfig?.fieldKey || 'name'
    const displayValue = option ? getDisplayValueFromField(option, fieldKey) : ''
    
    setSelectedIntegrationValue(option)
    setSelectedFieldValue(displayValue)
  }

  const showEventConfig = selectedEvent && eventsWithStepConfig.includes(selectedEvent)
  const showPhaseConfig = selectedEvent && eventsWithPhaseConfig.includes(selectedEvent)
  const showFieldConfig = selectedEvent && eventsWithFieldConfig.includes(selectedEvent)
  const showEventCard = selectedEvent !== null

  const isActionConfigured = () => {
    if (!selectedAction) return false
    
    if (selectedAction === 'create_card') {
      return !!actionStep && !!actionCardTitle
    }
    
    if (selectedAction === 'create_connected_card') {
      return !!actionPipeline && !!actionPipelineStep && !!actionCardTitle
    }
    
    if (selectedAction === 'move_card') {
      return !!actionPhase
    }
    
    if (selectedAction === 'update_field') {
      return !!actionField && !!actionVariable
    }
    
    if (selectedAction === 'create_formula') {
      return !!actionFormula && !!actionDestinationField
    }
    
    return true
  }

  const canShowAddCondition = () => {
    if (!selectedEvent) return false
    
    if (selectedEvent === 'card_created') {
      return !!selectedStep
    }
    
    if (selectedEvent === 'card_enter_phase' || selectedEvent === 'card_exit_phase') {
      return !!selectedPhase
    }
    
    if (selectedEvent === 'field_updated') {
      const hasValue = isIntegratedField() ? !!selectedIntegrationValue : !!selectedFieldValue
      return !!selectedStep && !!selectedField && hasValue
    }
    
    return true
  }

  const handleOpenConditionModal = () => {
    setEditingConditionId(null)
    setModalConditionRows([
      { id: `row_${Date.now()}`, field: 'valor', operator: 'greater_than', value: '' }
    ])
    setIsConditionModalOpen(true)
  }

  const handleEditCondition = (conditionId: string) => {
    const condition = conditions.find(c => c.id === conditionId)
    if (!condition) return

    setEditingConditionId(conditionId)
    setModalConditionRows([
      { id: condition.id, field: condition.field, operator: condition.operator, value: condition.value, logicOperator: condition.logicOperator }
    ])
    setIsConditionModalOpen(true)
  }

  const handleEditAllConditions = () => {
    if (conditions.length === 0) return

    setEditingConditionId('all')
    setModalConditionRows(
      conditions.map(c => ({
        id: c.id,
        field: c.field,
        operator: c.operator,
        value: c.value,
        logicOperator: c.logicOperator
      }))
    )
    setIsConditionModalOpen(true)
  }

  const handleCloseConditionModal = () => {
    setIsConditionModalOpen(false)
    setEditingConditionId(null)
    setModalConditionRows([
      { id: 'row_1', field: 'valor', operator: 'greater_than', value: '' }
    ])
  }

  const handleAddConditionRow = (logicOperator: 'AND' | 'OR', afterRowId: string) => {
    const rowIndex = modalConditionRows.findIndex(r => r.id === afterRowId)
    if (rowIndex === -1) return

    const updatedRows = [...modalConditionRows]
    updatedRows[rowIndex] = { ...updatedRows[rowIndex], logicOperator }
    
    const newRow: ConditionRow = {
      id: `row_${Date.now()}`,
      field: 'valor',
      operator: 'greater_than',
      value: ''
    }
    
    updatedRows.splice(rowIndex + 1, 0, newRow)
    setModalConditionRows(updatedRows)
  }

  const handleUpdateConditionRow = (rowId: string, field: keyof ConditionRow, value: string) => {
    setModalConditionRows(prev => prev.map(row => 
      row.id === rowId ? { ...row, [field]: value } : row
    ))
  }

  const handleRemoveConditionRow = (rowId: string) => {
    if (modalConditionRows.length <= 1) return
    
    const rowIndex = modalConditionRows.findIndex(r => r.id === rowId)
    const updatedRows = modalConditionRows.filter(r => r.id !== rowId)
    
    if (rowIndex === 0 && updatedRows.length > 0) {
      updatedRows[0] = { ...updatedRows[0], logicOperator: undefined }
    }
    
    setModalConditionRows(updatedRows)
  }

  const handleSaveCondition = () => {
    const validRows = modalConditionRows.filter(row => row.field && row.operator && row.value)
    if (validRows.length === 0) return

    if (editingConditionId === 'all') {
      const updatedConditions = validRows.map((row, index) => ({
        id: row.id || `cond_${Date.now()}_${index}`,
        field: row.field,
        operator: row.operator,
        value: row.value,
        logicOperator: row.logicOperator
      }))
      setConditions(updatedConditions)
    } else if (editingConditionId) {
      const firstRow = validRows[0]
      setConditions(prev => prev.map(c => 
        c.id === editingConditionId 
          ? { ...c, field: firstRow.field, operator: firstRow.operator, value: firstRow.value }
          : c
      ))
      
      if (validRows.length > 1) {
        const additionalConditions = validRows.slice(1).map((row, index) => ({
          id: `cond_${Date.now()}_${index}`,
          field: row.field,
          operator: row.operator,
          value: row.value,
          logicOperator: row.logicOperator
        }))
        setConditions(prev => [...prev, ...additionalConditions])
      }
    } else {
      const newConditions = validRows.map((row, index) => ({
        id: `cond_${Date.now()}_${index}`,
        field: row.field,
        operator: row.operator,
        value: row.value,
        logicOperator: index === 0 && conditions.length > 0 ? row.logicOperator : row.logicOperator
      }))
      setConditions(prev => [...prev, ...newConditions])
    }
    
    handleCloseConditionModal()
  }

  const handleRemoveCondition = (conditionId: string) => {
    setConditions(conditions.filter(c => c.id !== conditionId))
  }

  const getFieldLabel = (fieldValue: string) => {
    return fieldOptions.find(f => f.value === fieldValue)?.label || fieldValue
  }

  const getOperatorLabel = (operatorValue: string) => {
    return operatorOptions.find(o => o.value === operatorValue)?.label || operatorValue
  }

  console.log('[RENDER] Estados atuais:', { 
    selectedEvent, 
    selectedPhase, 
    selectedAction, 
    actionPipeline, 
    actionPipelineStep,
    showPhaseConfig,
    showEventConfig,
    stepsCount: steps.length 
  })

  return (
    <Container>
      <SubHeaderContainer>
        <LeftSide>
          <SectionName>{editingAutomationId ? 'Editar automação' : 'Nova automação'}</SectionName>
        </LeftSide>
        <RightSide>
          <CancelButton onClick={handleCancel} disabled={isCreating}>
            Cancelar
          </CancelButton>
          <CreateButton onClick={handleCreate} disabled={isCreating}>
            <Plus size={16} /> {isCreating ? (editingAutomationId ? 'Atualizando...' : 'Criando...') : (editingAutomationId ? 'Atualizar automação' : 'Criar automação')}
          </CreateButton>
        </RightSide>
      </SubHeaderContainer>

      <Breadcrumbs items={breadcrumbItems} />

      <Content>
        <FormCard>
          <FormGroup $flex={1}>
            <label>Nome da automação</label>
            <input 
              type="text" 
              placeholder="Digite aqui"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </FormGroup>
          <FormGroup $flex={2.5}>
            <label>Descrição</label>
            <input 
              type="text" 
              placeholder="Selecione"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </FormGroup>
        </FormCard>

        <SelectionContainer>
          {showEventCard ? (
            <EventConfigCard>
              <EventConfigHeader>
                <EventConfigTitle>
                  {getSelectedEventData() && (
                    <>
                      {React.createElement(getSelectedEventData()!.icon, { size: 18 })}
                      {selectedEvent === 'card_enter_phase' 
                        ? 'Quando o card entra na etapa' 
                        : selectedEvent === 'card_exit_phase'
                        ? 'Quando card sai da etapa'
                        : selectedEvent === 'field_updated'
                        ? 'Quando campo é atualizado'
                        : getSelectedEventData()!.label}
                    </>
                  )}
                </EventConfigTitle>
                <CloseButton onClick={handleCloseEventConfig}>
                  <X size={18} />
                </CloseButton>
              </EventConfigHeader>

              {showPhaseConfig && (
                <>
                  <SelectLabel>Para a etapa</SelectLabel>
                  <SelectWrapper>
                    <StyledSelect 
                      value={selectedPhase} 
                      onChange={(e) => setSelectedPhase(e.target.value)}
                    >
                      <option value="">Selecione</option>
                      {steps.map((step) => (
                        <option key={step.id} value={String(step.id)}>{step.name}</option>
                      ))}
                    </StyledSelect>
                    <SelectIcon>
                      <ChevronDown size={18} />
                    </SelectIcon>
                  </SelectWrapper>
                </>
              )}

              {showEventConfig && (
                <>
                  <SelectLabel>Etapa</SelectLabel>
                  <SelectWrapper>
                    <StyledSelect 
                      value={selectedStep} 
                      onChange={(e) => {
                        setSelectedStep(e.target.value)
                        setSelectedPhase('')
                        setSelectedField('')
                        setSelectedFieldValue('')
                        setSelectedIntegrationValue(null)
                      }}
                    >
                      <option value="">Selecione uma etapa</option>
                      {steps.map((step) => (
                        <option key={step.id} value={String(step.id)}>{step.name}</option>
                      ))}
                    </StyledSelect>
                    <SelectIcon>
                      <ChevronDown size={18} />
                    </SelectIcon>
                  </SelectWrapper>

                  {showFieldConfig && selectedStep && (
                    <>
                      <SelectLabel style={{ marginTop: '16px' }}>Campo</SelectLabel>
                      <SelectWrapper>
                        <StyledSelect 
                          value={selectedField} 
                          onChange={(e) => {
                            setSelectedField(e.target.value)
                            setSelectedFieldValue('')
                            setSelectedIntegrationValue(null)
                          }}
                        >
                          <option value="">Selecione</option>
                          {fields.map((field) => (
                            <option key={field.id} value={String(field.id)}>{field.name}</option>
                          ))}
                        </StyledSelect>
                        <SelectIcon>
                          <ChevronDown size={18} />
                        </SelectIcon>
                      </SelectWrapper>

                      {selectedField && (
                        <>
                          <SelectLabel style={{ marginTop: '16px' }}>Para o valor</SelectLabel>
                          {isIntegratedField() ? (
                            <InfiniteSelect
                              placeholder="Selecione"
                              value={selectedIntegrationValue}
                              onSelect={handleIntegrationSelect}
                              fetchFunction={fetchIntegrationData}
                              mode={getSelectedFieldData()?.integrationConfig?.integrationType === 'write' ? 'crud' : 'readonly'}
                              allowCreate={getSelectedFieldData()?.integrationConfig?.integrationType === 'write'}
                              allowDelete={false}
                              getOptionLabel={(option) => {
                                const fieldKey = getSelectedFieldData()?.integrationConfig?.fieldKey || 'name'
                                const colValue = getDisplayValueFromField(option, fieldKey)
                                if (colValue) {
                                  console.log('[getOptionLabel CA] moduleId:', getSelectedFieldData()?.integrationConfig?.moduleId, 'fieldKey:', fieldKey, 'colValue:', colValue, 'id:', option?.id)
                                  return colValue
                                }
                                const fallback = getPrimaryLabel(option)
                                console.log('[getOptionLabel CA] fallback moduleId:', getSelectedFieldData()?.integrationConfig?.moduleId, 'fieldKey:', fieldKey, 'fallback:', fallback, 'id:', option?.id)
                                return fallback
                              }}
                              endpointConfig={getSelectedFieldData()?.integrationConfig?.integrationType === 'write' ? {
                                listPath: getSelectedFieldData()?.integrationConfig?.endpoint,
                                createPath: getSelectedFieldData()?.integrationConfig?.createEndpoint || getSelectedFieldData()?.integrationConfig?.endpoint
                              } : undefined}
                            />
                          ) : (
                            <FieldInput
                              type={getSelectedFieldData()?.typeFieldId === 3 ? 'date' : 'text'}
                              placeholder="Digite"
                              value={selectedFieldValue}
                              onChange={(e) => setSelectedFieldValue(e.target.value)}
                            />
                          )}
                        </>
                      )}
                    </>
                  )}
                </>
              )}

              {(canShowAddCondition() || conditions.length > 0) && (
                <>
                  <ConditionsSeparator />

                  <ConditionsSection>
                    {conditions.length > 0 && (
                      <>
                        <ConditionsSectionTitle>Condicionais</ConditionsSectionTitle>
                        <ConditionsGroupContainer>
                          <ConditionsGroupHeader>
                            <ConditionRow>
                              <strong>{getFieldLabel(conditions[0].field)}</strong>
                              <span>{getOperatorLabel(conditions[0].operator)}</span>
                              <strong>{conditions[0].value}</strong>
                            </ConditionRow>
                            <ConditionChipActions onClick={handleEditAllConditions}>
                              <MoreHorizontal size={18} />
                            </ConditionChipActions>
                          </ConditionsGroupHeader>
                          {conditions.length > 1 && (
                            <ConditionsGroupContent>
                              {conditions.slice(1).map((condition, index) => (
                                <React.Fragment key={condition.id}>
                                  {conditions[index].logicOperator && (
                                    <LogicOperatorBetween>
                                      {conditions[index].logicOperator === 'AND' ? 'E' : 'Ou'}
                                    </LogicOperatorBetween>
                                  )}
                                  <ConditionRow>
                                    <strong>{getFieldLabel(condition.field)}</strong>
                                    <span>{getOperatorLabel(condition.operator)}</span>
                                    <strong>{condition.value}</strong>
                                  </ConditionRow>
                                </React.Fragment>
                              ))}
                            </ConditionsGroupContent>
                          )}
                        </ConditionsGroupContainer>
                      </>
                    )}
                    {canShowAddCondition() && (
                      <AddConditionButton onClick={handleOpenConditionModal}>
                        <Plus size={16} />
                        Adicionar condição
                      </AddConditionButton>
                    )}
                  </ConditionsSection>
                </>
              )}
            </EventConfigCard>
          ) : (
            <SelectionCard>
              <SelectionTitle>Selecione o evento</SelectionTitle>
              <SelectionList>
                {events.map((event) => (
                  <SelectionItem 
                    key={event.id}
                    $selected={selectedEvent === event.id}
                    onClick={() => handleEventSelect(event.id)}
                  >
                    <event.icon />
                    <span>{event.label}</span>
                  </SelectionItem>
                ))}
              </SelectionList>
            </SelectionCard>
          )}

          {showActionConfig ? (
            <EventConfigCard>
              <EventConfigHeader>
                <EventConfigTitle>
                  {(() => {
                    const ActionIcon = getSelectedActionData()?.icon
                    return ActionIcon ? <ActionIcon size={20} /> : null
                  })()}
                  {getSelectedActionData()?.label}
                </EventConfigTitle>
                <CloseButton onClick={handleCloseActionConfig}>
                  <X size={18} />
                </CloseButton>
              </EventConfigHeader>

              {selectedAction === 'create_card' && (
                <>
                  <SelectLabel>Etapa</SelectLabel>
                  <SelectWrapper>
                    <StyledSelect 
                      value={actionStep} 
                      onChange={(e) => {
                        setActionStep(e.target.value)
                        setActionCardTitle('')
                      }}
                    >
                      <option value="">Selecione uma etapa</option>
                      {steps.map((step) => (
                        <option key={step.id} value={String(step.id)}>{step.name}</option>
                      ))}
                    </StyledSelect>
                    <SelectIcon>
                      <ChevronDown size={18} />
                    </SelectIcon>
                  </SelectWrapper>

                  {actionStep && (
                    <>
                      <SelectLabel style={{ marginTop: '16px' }}>Título do card</SelectLabel>
                      <FieldInput
                        type="text"
                        placeholder="Digite o título do card"
                        value={actionCardTitle}
                        onChange={(e) => setActionCardTitle(e.target.value)}
                      />
                    </>
                  )}
                </>
              )}

              {selectedAction === 'create_connected_card' && (
                <>
                  <SelectLabel>No funil</SelectLabel>
                  <SelectWrapper>
                    <StyledSelect 
                      value={actionPipeline} 
                      onChange={(e) => {
                        setActionPipeline(e.target.value)
                        setActionPipelineStep('')
                      }}
                    >
                      <option value="">Selecione</option>
                      {pipelines.map((pipeline) => (
                        <option key={pipeline.id} value={String(pipeline.id)}>{pipeline.name}</option>
                      ))}
                    </StyledSelect>
                    <SelectIcon>
                      <ChevronDown size={18} />
                    </SelectIcon>
                  </SelectWrapper>

                  {actionPipeline && (
                    <>
                      <SelectLabel style={{ marginTop: '16px' }}>Etapa</SelectLabel>
                      <SelectWrapper>
                        <StyledSelect 
                          value={actionPipelineStep} 
                          onChange={(e) => setActionPipelineStep(e.target.value)}
                        >
                          <option value="">Selecione</option>
                          {actionPipelineSteps.map((step) => (
                            <option key={step.id} value={String(step.id)}>{step.name}</option>
                          ))}
                        </StyledSelect>
                        <SelectIcon>
                          <ChevronDown size={18} />
                        </SelectIcon>
                      </SelectWrapper>

                      {actionPipelineStep && (
                        <>
                          <SelectLabel style={{ marginTop: '16px' }}>Título do card</SelectLabel>
                          <FieldInput
                            type="text"
                            placeholder="Digite o título do card"
                            value={actionCardTitle}
                            onChange={(e) => setActionCardTitle(e.target.value)}
                          />
                        </>
                      )}
                    </>
                  )}
                </>
              )}

              {selectedAction === 'update_field' && (
                <>
                  <SelectLabel>Card</SelectLabel>
                  <SelectWrapper>
                    <StyledSelect 
                      value={actionCard} 
                      onChange={(e) => {
                        setActionCard(e.target.value)
                        setActionField('')
                        setActionVariable('')
                      }}
                    >
                      <option value="">Selecione</option>
                      {cards.map((card) => (
                        <option key={card.id} value={card.id}>{card.title}</option>
                      ))}
                    </StyledSelect>
                    <SelectIcon>
                      <ChevronDown size={18} />
                    </SelectIcon>
                  </SelectWrapper>

                  {actionCard && (
                    <>
                      <SelectLabel style={{ marginTop: '16px' }}>Campo</SelectLabel>
                      <SelectWrapper>
                        <StyledSelect 
                          value={actionField} 
                          onChange={(e) => {
                            setActionField(e.target.value)
                            setActionVariable('')
                          }}
                        >
                          <option value="">Selecione</option>
                          {actionFields.map((field) => (
                            <option key={field.id} value={String(field.id)}>{field.name}</option>
                          ))}
                        </StyledSelect>
                        <SelectIcon>
                          <ChevronDown size={18} />
                        </SelectIcon>
                      </SelectWrapper>

                      {actionField && (
                        <>
                          <SelectLabel style={{ marginTop: '16px' }}>Variável</SelectLabel>
                          <SelectWrapper>
                            <StyledSelect 
                              value={actionVariable} 
                              onChange={(e) => setActionVariable(e.target.value)}
                            >
                              <option value="">Selecione</option>
                              {variableOptions.map((variable) => (
                                <option key={variable.value} value={variable.value}>{variable.label}</option>
                              ))}
                            </StyledSelect>
                            <SelectIcon>
                              <ChevronDown size={18} />
                            </SelectIcon>
                          </SelectWrapper>
                        </>
                      )}
                    </>
                  )}
                </>
              )}

              {selectedAction === 'move_card' && (
                <>
                  <SelectLabel>Card</SelectLabel>
                  <SelectWrapper>
                    <StyledSelect 
                      value={actionCard} 
                      onChange={(e) => {
                        setActionCard(e.target.value)
                        setActionPhase('')
                      }}
                    >
                      <option value="">Selecione</option>
                      {cards.map((card) => (
                        <option key={card.id} value={card.id}>{card.title}</option>
                      ))}
                    </StyledSelect>
                    <SelectIcon>
                      <ChevronDown size={18} />
                    </SelectIcon>
                  </SelectWrapper>

                  {actionCard && (
                    <>
                      <SelectLabel style={{ marginTop: '16px' }}>Para a etapa</SelectLabel>
                      <SelectWrapper>
                        <StyledSelect 
                          value={actionPhase} 
                          onChange={(e) => setActionPhase(e.target.value)}
                        >
                          <option value="">Selecione</option>
                          {steps.map((step) => (
                            <option key={step.id} value={String(step.id)}>{step.name}</option>
                          ))}
                        </StyledSelect>
                        <SelectIcon>
                          <ChevronDown size={18} />
                        </SelectIcon>
                      </SelectWrapper>
                    </>
                  )}
                </>
              )}

              {selectedAction === 'create_formula' && (
                <>
                  <SelectLabel>Fórmula</SelectLabel>
                  <SearchInputWrapper>
                    <SearchInput
                      type="text"
                      placeholder="Value"
                      value={actionFormula}
                      onChange={(e) => setActionFormula(e.target.value)}
                    />
                    <SearchIcon>
                      <Search size={18} />
                    </SearchIcon>
                  </SearchInputWrapper>

                  {actionFormula && (
                    <>
                      <SelectLabel style={{ marginTop: '16px' }}>Campo de destino</SelectLabel>
                      <SelectWrapper>
                        <StyledSelect 
                          value={actionDestinationField} 
                          onChange={(e) => setActionDestinationField(e.target.value)}
                        >
                          <option value="">Selecione</option>
                          {actionFields.map((field) => (
                            <option key={field.id} value={String(field.id)}>{field.name}</option>
                          ))}
                        </StyledSelect>
                        <SelectIcon>
                          <ChevronDown size={18} />
                        </SelectIcon>
                      </SelectWrapper>
                    </>
                  )}
                </>
              )}

              {(isActionConfigured() || conditions.length > 0) && (
                <>
                  <ConditionsSeparator />

                  <ConditionsSection>
                    {conditions.length > 0 && (
                      <>
                        <ConditionsSectionTitle>Condicionais</ConditionsSectionTitle>
                        <ConditionsGroupContainer>
                          <ConditionsGroupHeader>
                            <ConditionRow>
                              <strong>{getFieldLabel(conditions[0].field)}</strong>
                              <span>{getOperatorLabel(conditions[0].operator)}</span>
                              <strong>{conditions[0].value}</strong>
                            </ConditionRow>
                            <ConditionChipActions onClick={handleEditAllConditions}>
                              <MoreHorizontal size={18} />
                            </ConditionChipActions>
                          </ConditionsGroupHeader>
                          {conditions.length > 1 && (
                            <ConditionsGroupContent>
                              {conditions.slice(1).map((condition, index) => (
                                <React.Fragment key={condition.id}>
                                  {conditions[index].logicOperator && (
                                    <LogicOperatorBetween>
                                      {conditions[index].logicOperator === 'AND' ? 'E' : 'Ou'}
                                    </LogicOperatorBetween>
                                  )}
                                  <ConditionRow>
                                    <strong>{getFieldLabel(condition.field)}</strong>
                                    <span>{getOperatorLabel(condition.operator)}</span>
                                    <strong>{condition.value}</strong>
                                  </ConditionRow>
                                </React.Fragment>
                              ))}
                            </ConditionsGroupContent>
                          )}
                        </ConditionsGroupContainer>
                      </>
                    )}
                    {isActionConfigured() && (
                      <AddConditionButton onClick={handleOpenConditionModal}>
                        <Plus size={16} />
                        Adicionar condição
                      </AddConditionButton>
                    )}
                  </ConditionsSection>
                </>
              )}
            </EventConfigCard>
          ) : (
            <SelectionCard>
              <SelectionTitle>Selecione a ação</SelectionTitle>
              <SelectionList>
                {actions.map((action) => (
                  <SelectionItem 
                    key={action.id}
                    $selected={selectedAction === action.id}
                    $disabled={!selectedEvent}
                    onClick={() => selectedEvent && handleActionSelect(action.id)}
                  >
                    <action.icon />
                    <span>{action.label}</span>
                  </SelectionItem>
                ))}
              </SelectionList>
            </SelectionCard>
          )}
        </SelectionContainer>
      </Content>

      {isConditionModalOpen && (
        <ModalOverlay onClick={handleCloseConditionModal}>
          <ModalContainer onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>{editingConditionId ? 'Editar condição' : 'Adicionar condição'}</ModalTitle>
              <ModalCloseButton onClick={handleCloseConditionModal}>
                <X size={20} />
              </ModalCloseButton>
            </ModalHeader>
            <ModalSubtitle>Selecione os parâmetros</ModalSubtitle>

            {modalConditionRows.map((row, index) => (
              <React.Fragment key={row.id}>
                <ModalFieldsRow>
                  <ModalFieldGroup $flex={1.2}>
                    {index === 0 && <ModalFieldLabel>Campo</ModalFieldLabel>}
                    {index > 0 && <ModalFieldLabel>&nbsp;</ModalFieldLabel>}
                    <ModalSelectWrapper>
                      <ModalSelect 
                        value={row.field}
                        onChange={(e) => handleUpdateConditionRow(row.id, 'field', e.target.value)}
                      >
                        {fieldOptions.map((field) => (
                          <option key={field.value} value={field.value}>{field.label}</option>
                        ))}
                      </ModalSelect>
                      <ModalSelectIcon>
                        <ChevronDown size={18} />
                      </ModalSelectIcon>
                    </ModalSelectWrapper>
                  </ModalFieldGroup>

                  <ModalFieldGroup $flex={1}>
                    {index === 0 && <ModalFieldLabel>&nbsp;</ModalFieldLabel>}
                    {index > 0 && <ModalFieldLabel>&nbsp;</ModalFieldLabel>}
                    <ModalSelectWrapper>
                      <ModalSelect 
                        value={row.operator}
                        onChange={(e) => handleUpdateConditionRow(row.id, 'operator', e.target.value)}
                      >
                        {operatorOptions.map((op) => (
                          <option key={op.value} value={op.value}>{op.label}</option>
                        ))}
                      </ModalSelect>
                      <ModalSelectIcon>
                        <ChevronDown size={18} />
                      </ModalSelectIcon>
                    </ModalSelectWrapper>
                  </ModalFieldGroup>

                  <ModalFieldGroup $flex={1.2}>
                    {index === 0 && <ModalFieldLabel>Valor</ModalFieldLabel>}
                    {index > 0 && <ModalFieldLabel>&nbsp;</ModalFieldLabel>}
                    <ModalInput
                      type="text"
                      placeholder="R$ 123,45"
                      value={row.value}
                      onChange={(e) => handleUpdateConditionRow(row.id, 'value', e.target.value)}
                    />
                  </ModalFieldGroup>

                  {modalConditionRows.length > 1 && (
                    <RemoveRowButton onClick={() => handleRemoveConditionRow(row.id)}>
                      <X size={16} />
                    </RemoveRowButton>
                  )}
                </ModalFieldsRow>

                {index < modalConditionRows.length - 1 && row.logicOperator && (
                  <LogicOperatorBetween>{row.logicOperator === 'AND' ? 'E' : 'Ou'}</LogicOperatorBetween>
                )}

                {index === modalConditionRows.length - 1 && (
                  <LogicButtonsRow>
                    <LogicButton 
                      $active={false}
                      onClick={() => handleAddConditionRow('AND', row.id)}
                    >
                      <Plus size={14} />
                      E
                    </LogicButton>
                    <LogicButton 
                      $active={false}
                      onClick={() => handleAddConditionRow('OR', row.id)}
                    >
                      <Plus size={14} />
                      Ou
                    </LogicButton>
                  </LogicButtonsRow>
                )}
              </React.Fragment>
            ))}

            <ModalFooter>
              {editingConditionId === 'all' && (
                <ModalDeleteButton onClick={() => {
                  setConditions([])
                  handleCloseConditionModal()
                }}>
                  <Trash2 size={16} />
                  Excluir
                </ModalDeleteButton>
              )}
              <ModalCancelButton onClick={handleCloseConditionModal}>
                Cancelar
              </ModalCancelButton>
              <ModalAddButton onClick={handleSaveCondition}>
                <Plus size={16} />
                {editingConditionId ? 'Atualizar' : 'Adicionar'}
              </ModalAddButton>
            </ModalFooter>
          </ModalContainer>
        </ModalOverlay>
      )}
    </Container>
  )
}

export default CreateAutomation
